import { SignInOptions } from 'next-auth/react';
import { TreatmentWithConfig } from '@splitsoftware/splitio/types/splitio';
import * as ExperimentService from 'src/services/experiments/ExperimentService.client';
import { ZillowHomeLoansURL } from 'src/utils/ZillowHomeLoansURL';
import * as ApplicationConfigurationService from 'src/services/config/ApplicationConfigurationService.client';
import * as RouteConfigurationService from 'src/services/config/domain/RouteConfigurationService/RouteConfigurationService.client';
import { setRedirectLocation } from './setRedirectLocation';
import { zillowSignIn } from './zillowSignIn/zillowSignIn';

declare global {
    interface Window {
        SESSION_START_REQUESTED: boolean;
    }
}

/**
 * Since multiple components and business logic paths can cause a sign in attempt,
 * it can be difficult to debug why session logins were attempted. To address this, this function
 * requires that a justification for performing a login is provided, which is then logged to the console in lower
 * environments for debugging.
 *
 * Additionally, this function abstracts the logic around ensuring we leverage the required "signin bounce" experience
 * that is aimed at resolving the back-button-breaking-on-authorization-code-flow-pages problem.
 *
 * @param justification - why the session should be started
 */
export const handleSessionStart = (
    justification: string,
    experiments: Record<ExperimentService.Domain.ActiveExperiment, TreatmentWithConfig | null>,
    signInOptions: SignInOptions = {},
    authorizationParams: Record<string, string> = {}
) => {
    if (window.SESSION_START_REQUESTED) {
        return;
    }
    window.SESSION_START_REQUESTED = true;
    const configService = ApplicationConfigurationService.ConfigProvider.getInstance();
    if (configService.runtimeConfig.isLowerEnvironment) {
        // eslint-disable-next-line no-console
        console.info('Application Session Started - ', justification);
    }

    const signinBouncePage = new ZillowHomeLoansURL(
        RouteConfigurationService.Domain.ApplicationRoute.AUTH_SIGNIN_BOUNCE,
        {
            useLocal: true,
        }
    ).resolvedURL;
    if (window.location.pathname !== signinBouncePage) {
        setRedirectLocation(window.location.pathname);
    }

    zillowSignIn(experiments, signInOptions, authorizationParams);
};
